import React, { useContext, useState, useEffect } from "react";
import { RemixBuilderContext } from "../remix/shared/RemixBuilderContext";
import CFInput from "../shared/forms/CFInput";
import CFToggle from "../shared/forms/CFToggle";
import CFSelect from "../shared/forms/CFSelect";
import Tooltip from "../shared/Tooltip";
import useActionCable from "../shared/useActionCable";
import apiRequest from "../../utils/apiRequest";
import {
  canUseAiRemixTemplate,
  remixAiUpgradePlan,
} from "../../utils/canUseAiRemixText";
import AiInstructionAssetUploader from "../remix/shared/AiInstructionAssetUploader";

const REMIX_SPARKLE_ICON = (
  <svg
    width="13"
    height="14"
    viewBox="0 0 13 14"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className="flex-shrink-0"
  >
    <path
      d="M10.5086 7.86766L8.97152 7.42808C8.27936 7.23279 7.64886 6.86299 7.14034 6.35408C6.63183 5.84516 6.26232 5.21416 6.06718 4.52145L5.62795 2.9832C5.60586 2.92939 5.56829 2.88336 5.52 2.85096C5.47171 2.81857 5.41488 2.80127 5.35675 2.80127C5.29861 2.80127 5.24179 2.81857 5.1935 2.85096C5.14521 2.88336 5.10763 2.92939 5.08554 2.9832L4.64631 4.52145C4.45117 5.21416 4.08166 5.84516 3.57315 6.35408C3.06464 6.86299 2.43413 7.23279 1.74197 7.42808L0.204942 7.86766C0.145925 7.88443 0.0939831 7.92 0.0569971 7.96898C0.0200111 8.01797 0 8.07769 0 8.13908C0 8.20048 0.0200111 8.2602 0.0569971 8.30918C0.0939831 8.35817 0.145925 8.39374 0.204942 8.4105L1.74197 8.85008C2.43413 9.04538 3.06464 9.41517 3.57315 9.92409C4.08166 10.433 4.45117 11.064 4.64631 11.7567L5.08554 13.295C5.10229 13.354 5.13783 13.406 5.18678 13.443C5.23572 13.48 5.2954 13.5001 5.35675 13.5001C5.41809 13.5001 5.47777 13.48 5.52671 13.443C5.57566 13.406 5.6112 13.354 5.62795 13.295L6.06718 11.7567C6.26232 11.064 6.63183 10.433 7.14034 9.92409C7.64886 9.41517 8.27936 9.04538 8.97152 8.85008L10.5086 8.4105C10.5676 8.39374 10.6195 8.35817 10.6565 8.30918C10.6935 8.2602 10.7135 8.20048 10.7135 8.13908C10.7135 8.07769 10.6935 8.01797 10.6565 7.96898C10.6195 7.92 10.5676 7.88443 10.5086 7.86766Z"
      fill="currentColor"
    />
    <path
      d="M12.9087 2.75173L12.2256 2.55636C11.918 2.46956 11.6378 2.30521 11.4118 2.07903C11.1858 1.85284 11.0215 1.5724 10.9348 1.26452L10.7396 0.58086C10.7298 0.556941 10.7131 0.536483 10.6916 0.522085C10.6702 0.507687 10.6449 0.5 10.6191 0.5C10.5932 0.5 10.568 0.507687 10.5465 0.522085C10.525 0.536483 10.5083 0.556941 10.4985 0.58086L10.3033 1.26452C10.2166 1.5724 10.0524 1.85284 9.82635 2.07903C9.60034 2.30521 9.32012 2.46956 9.01249 2.55636L8.32937 2.75173C8.30314 2.75918 8.28005 2.77499 8.26361 2.79676C8.24718 2.81853 8.23828 2.84507 8.23828 2.87236C8.23828 2.89965 8.24718 2.92619 8.26361 2.94796C8.28005 2.96973 8.30314 2.98554 8.32937 2.99299L9.01249 3.18836C9.32012 3.27516 9.60034 3.43951 9.82635 3.6657C10.0524 3.89188 10.2166 4.17233 10.3033 4.4802L10.4985 5.16386C10.506 5.19011 10.5218 5.21322 10.5435 5.22967C10.5653 5.24612 10.5918 5.25502 10.6191 5.25502C10.6463 5.25502 10.6728 5.24612 10.6946 5.22967C10.7164 5.21322 10.7321 5.19011 10.7396 5.16386L10.9348 4.4802C11.0215 4.17233 11.1858 3.89188 11.4118 3.6657C11.6378 3.43951 11.918 3.27516 12.2256 3.18836L12.9087 2.99299C12.935 2.98554 12.9581 2.96973 12.9745 2.94796C12.9909 2.92619 12.9998 2.89965 12.9998 2.87236C12.9998 2.84507 12.9909 2.81853 12.9745 2.79676C12.9581 2.77499 12.935 2.75918 12.9087 2.75173Z"
      fill="currentColor"
    />
  </svg>
);

const ELEMENT_TYPE_OPTIONS = [
  { value: "headline", label: "Headlines" },
  { value: "text", label: "Text" },
  { value: "button", label: "Buttons" },
  { value: "form", label: "Forms" },
  { value: "survey", label: "Surveys" },
  { value: "quiz", label: "Quizzes" },
  { value: "list", label: "Lists" },
  { value: "collapse", label: "Collapses" },
  { value: "tabs", label: "Tabs" },
  { value: "carousel", label: "Carousels" },
  { value: "product_hero", label: "Product Heroes" },
  { value: "products", label: "Products" },
];

const VariantTemplatePreviewModalSettingsDetails = (props) => {
  const {
    templateData,
    cta,
    createVariantPath,
    onClose,
    onSuccess,
    initialRemixPrompt,
  } = props;
  const [contextData, builder] = useContext(RemixBuilderContext);
  const [loadingNextStep, setLoadingNextStep] = useState(false);
  const [cableSyncId, setCableSyncId] = useState(null);
  const [isAiRemixing, setIsAiRemixing] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  useEffect(() => {
    if (!initialRemixPrompt || !builder) return;

    const canAutoEnableAiRemix = canUseAiRemixTemplate({
      currentUser: contextData?.current_user,
      website: contextData?.website,
      subscription: contextData?.subscription,
      account: contextData?.account,
      template: templateData?.template,
    });

    builder.updateRemixSettings("options", {
      ...(contextData?.remix_settings?.options || {}),
      remix_all_text: canAutoEnableAiRemix,
      remix_tone: initialRemixPrompt,
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const { lastMessage: rewriteProgress } = useActionCable(
    isAiRemixing ? "ProgressValueChannel" : null,
    { id: cableSyncId },
    {
      autoDisconnect: true,
      autoDisconnectCondition: (value) => value === 100,
    }
  );

  useActionCable(
    cableSyncId && loadingNextStep
      ? "AsyncModalRequestConfirmationChannel"
      : null,
    { id: cableSyncId },
    {
      onReceived: (data) => {
        if (data?.status === "ready") {
          const redirectUrl =
            data?.builder_url ||
            (data?.object?.id && cta?.id && contextData.website?.id
              ? `/websites/${contextData.website.id}/cta/${cta.id}/variants/${data.object.id}/edit`
              : null);
          if (redirectUrl) {
            // Redirect without closing so the loader stays visible until navigation
            window.location.href = redirectUrl;
          } else if (onSuccess && typeof onSuccess === "function") {
            onClose();
            onSuccess(data);
          }
        } else if (data?.status === "failed") {
          setLoadingNextStep(false);
          setIsAiRemixing(false);
          setCableSyncId(null);
          builder.setRemixLoading(null);
          setErrorMessage(data?.error || "Failed to create variant.");
        }
      },
      autoDisconnect: true,
      autoDisconnectCondition: (data) =>
        data?.status === "ready" || data?.status === "failed",
    }
  );

  const currentUser = contextData.current_user;
  const website = contextData.website;

  const remixSettings = contextData.remix_settings ?? {};
  const options = remixSettings.options ?? {
    remix_all_text: false,
    remix_tone: "",
    remix_asset_urls: [],
    excluded_element_types: [],
  };

  const buildRemixParams = () => {
    const aiRemixEnabled = options.remix_all_text === true;
    const rawExcluded = options.excluded_element_types;
    const excluded_element_types =
      aiRemixEnabled && Array.isArray(rawExcluded)
        ? rawExcluded
            .map((o) => (typeof o === "string" ? o : o?.value))
            .filter(Boolean)
        : [];

    return {
      remix_styles: "true",
      remix_all_text: aiRemixEnabled ? "true" : "false",
      remix_tone: aiRemixEnabled ? options.remix_tone || "" : "",
      remix_asset_urls:
        aiRemixEnabled && Array.isArray(options.remix_asset_urls)
          ? options.remix_asset_urls
          : [],
      remix_instructions: aiRemixEnabled
        ? options.remix_instructions || ""
        : "",
      excluded_element_types,
      "heading-font-family":
        remixSettings.styles?.["heading-font-family"]?.value ||
        remixSettings.styles?.["heading-font-family"],
      "heading-font-color":
        remixSettings.styles?.["heading-font-color"]?.hex ||
        remixSettings.styles?.["heading-font-color"],
      "paragraph-font-family":
        remixSettings.styles?.["paragraph-font-family"]?.value ||
        remixSettings.styles?.["paragraph-font-family"],
      "paragraph-font-color":
        remixSettings.styles?.["paragraph-font-color"]?.hex ||
        remixSettings.styles?.["paragraph-font-color"],
      "background-color":
        remixSettings.styles?.["background-color"]?.hex ||
        remixSettings.styles?.["background-color"],
      "button-color":
        remixSettings.styles?.["button-color"]?.hex ||
        remixSettings.styles?.["button-color"],
      "accent-color":
        remixSettings.styles?.["accent-color"]?.hex ||
        remixSettings.styles?.["accent-color"],
      "logo-style": remixSettings.styles?.["logo-style"],
      "logo-image": remixSettings.styles?.["logo-image"],
    };
  };

  const handleNextStep = async () => {
    if (loadingNextStep || !templateData || !createVariantPath || !cta) return;

    setLoadingNextStep(true);
    setErrorMessage(null);

    const remixOptions = options;
    const willAiRemix =
      remixOptions.remix_all_text === true &&
      (remixOptions.remix_tone || "").trim().length > 0;

    const syncId = Math.random().toString(36).substring(2, 15);
    setCableSyncId(syncId);
    if (willAiRemix) {
      setIsAiRemixing(true);
    }

    const remixParams = buildRemixParams();

    try {
      await new Promise((resolve) => setTimeout(resolve, 500));
      const response = await apiRequest("POST", createVariantPath, {
        variant: {
          template: templateData.object.id,
          cta_id: cta.id,
        },
        website_id: website.id,
        variant_name: remixSettings.variant_name ?? "",
        cable_sync_id: syncId,
        remix: remixParams,
      });

      if (response?.code >= 200 && response?.code < 300) {
        return;
      }
      setLoadingNextStep(false);
      setIsAiRemixing(false);
      setCableSyncId(null);
      builder.setRemixLoading(null);
      setErrorMessage(
        response?.json?.error || "Failed to create variant. Please try again."
      );
    } catch (err) {
      setLoadingNextStep(false);
      setIsAiRemixing(false);
      setCableSyncId(null);
      builder.setRemixLoading(null);
      setErrorMessage("Failed to create variant. Please try again.");
    }
  };

  useEffect(() => {
    if (!loadingNextStep) {
      builder.setRemixLoading(null);
      return;
    }
    const willAiRemix =
      options?.remix_all_text === true &&
      (options?.remix_tone || "").trim().length > 0;
    builder.setRemixLoading({
      loading: true,
      progress: willAiRemix ? rewriteProgress : null,
      isAiRemixing: willAiRemix,
    });
  }, [loadingNextStep, rewriteProgress, isAiRemixing, options]);

  const handlePreviousStep = () => {
    if (loadingNextStep) return;
    setErrorMessage(null);
    builder.goToPreviousStep();
  };

  return (
    <React.Fragment>
      <div className="remix-editor-settings-layouts flex flex-column overflow-scroll grow-1 min-w-0">
        <h2 className="text-[18px] font-semibold mt-15">Create your variant</h2>
        <p className="text-[12px] text-gray-400">
          Give your variant a name and click to create
        </p>

        <div className="min-w-0 mt-5">
          <div className="mb-[5px] col-xs-9 no-padding-sides">
            <span className="setting-label text-xl text-black dark:text-white capitalize whitespace-nowrap">
              Variant name
            </span>
          </div>

          <CFInput
            name="variant_name"
            placeholder="Name your split-test"
            value={remixSettings.variant_name ?? ""}
            onChange={(value) => {
              builder.updateRemixSettings("variant_name", value);
            }}
          />
        </div>

        <div className="min-w-0 mt-5">
          <div className="flex items-center gap-2">
            <span className="setting-label text-xl text-black dark:text-white">
              AI Remix
            </span>

            <Tooltip
              tooltip={`Toggle this to use the remix AI instructions<br> to AI remix all text in the variant.`}
            />

            <span className="bg-blue-600 py-1 px-2.5 rounded text-sm text-white font-medium">
              BETA
            </span>
          </div>

          <div className="mt-2">
            <CFToggle
              name="remix_all_text"
              checked={options.remix_all_text === true}
              onChange={(checked, e) => {
                const isEnabled = canUseAiRemixTemplate({
                  currentUser,
                  website,
                  subscription: contextData.subscription,
                  account: contextData.account,
                  template: templateData?.template,
                });

                if (!isEnabled) {
                  if (checked) {
                    if (
                      typeof window.subscriptions?.upgrade_modal === "function"
                    ) {
                      window.subscriptions.upgrade_modal(
                        e ?? null,
                        false,
                        "use remix AI to generate a template",
                        remixAiUpgradePlan({
                          subscription: contextData.subscription,
                          website,
                        }),
                        "Triggered feature CTA – Remix AI",
                        false,
                        "x3rs6cvrwi"
                      );
                    }
                  } else {
                    builder.updateRemixSettings("options", {
                      ...options,
                      remix_all_text: false,
                    });
                  }
                  return;
                }

                builder.updateRemixSettings("options", {
                  ...options,
                  remix_all_text: checked,
                });
              }}
            />
            {options.remix_all_text === true &&
              Number(templateData?.template?.ai_remix_actions_estimate) > 0 && (
                <p className="mt-1.5 mb-0 text-sm text-left text-gray-500 dark:text-slate-400 leading-snug">
                  Uses about {templateData.template.ai_remix_actions_estimate}{" "}
                  actions
                </p>
              )}
          </div>
        </div>

        {options.remix_all_text === true && (
          <div className="min-w-0 mt-5">
            <div className="mb-[5px] col-xs-9 no-padding-sides">
              <span className="setting-label text-xl text-black dark:text-white capitalize whitespace-nowrap text-left">
                Copywriting Remix AI Instructions{" "}
                <Tooltip
                  tooltip={`Describe the tone for this variant<br> for remixing text with AI.`}
                />
              </span>
            </div>

            <div className="relative">
              <textarea
                name="remix_tone"
                rows={6}
                placeholder="Urgency for Black Friday/Cyber Monday."
                value={options.remix_tone ?? ""}
                onChange={(e) => {
                  builder.updateRemixSettings("options", {
                    ...options,
                    remix_tone: e.target.value,
                  });
                }}
                className="w-full p-3 pb-[64px] bg-white dark:bg-slate-950 text-xl text-black dark:text-white rounded-xl border-solid border-[1px] border-slate-200 dark:border-slate-700 focus:border-slate-200 outline-none focus:outline-none focus:ring-0 appearance-none resize-y min-h-[6rem]"
              />

              <div className="absolute left-3 bottom-3 z-10">
                <AiInstructionAssetUploader
                  assetUrls={options.remix_asset_urls ?? []}
                  websiteId={website?.id}
                  environment={contextData.environment}
                  hidden={loadingNextStep}
                  onChange={(assetUrls) => {
                    builder.updateRemixSettings("options", {
                      ...options,
                      remix_asset_urls: assetUrls,
                    });
                  }}
                />
              </div>
            </div>
          </div>
        )}

        {options.remix_all_text === true && (
          <div className="min-w-0 mt-5">
            <div className="mb-[5px] col-xs-9 no-padding-sides">
              <span className="setting-label text-xl text-black dark:text-white capitalize whitespace-nowrap text-left">
                Exclude Elements From Remixing{" "}
                <Tooltip tooltip="Select element types to exclude from AI text remixing." />
              </span>
            </div>

            <CFSelect
              options={ELEMENT_TYPE_OPTIONS}
              value={options.excluded_element_types ?? []}
              onChange={(selected) => {
                builder.updateRemixSettings("options", {
                  ...options,
                  excluded_element_types: selected ?? [],
                });
              }}
              isMulti={true}
              placeholder="Select types to exclude..."
              isClearable={true}
            />
          </div>
        )}
      </div>

      {errorMessage && (
        <div
          className="col-xs-12 p-4 mb-4 rounded-md bg-red-50 border border-red-200 text-red-800"
          role="alert"
        >
          {errorMessage}
        </div>
      )}

      <div className="remix-editor-settings-buttons pt-[30px] pb-[30px] gap-4 flex justify-between">
        <div
          onClick={handlePreviousStep}
          className={`cursor-pointer rounded-xl w-full p-[10px] flex justify-center items-center text-xl border border-solid border-slate-200 bg-white text-black ${
            loadingNextStep ? "disabled" : ""
          }`}
        >
          Back
        </div>
        {(() => {
          const willShowPurple = options.remix_all_text === true;
          const usePurpleButton = willShowPurple || isAiRemixing;
          return (
            <div
              onClick={handleNextStep}
              className={`cursor-pointer rounded-xl w-full p-[10px] flex justify-center items-center gap-2 text-xl text-white ${
                loadingNextStep ? "disabled" : ""
              } ${
                usePurpleButton
                  ? "border-0"
                  : "border border-solid border-blue-700 bg-blue-600"
              }`}
              style={
                usePurpleButton
                  ? {
                      background:
                        "linear-gradient(135deg, #4f6fff 0%, #003FFF 100%)",
                    }
                  : {}
              }
            >
              {loadingNextStep ? (
                <span className="flex items-center gap-2">
                  <span className="cf-loading-spinner" />
                  {isAiRemixing && (
                    <span>
                      {rewriteProgress !== null
                        ? `Remixing... ${rewriteProgress}%`
                        : "Remixing..."}
                    </span>
                  )}
                </span>
              ) : usePurpleButton ? (
                <>
                  {REMIX_SPARKLE_ICON}
                  Remix Template
                </>
              ) : (
                "Create Variant +"
              )}
            </div>
          );
        })()}
      </div>
    </React.Fragment>
  );
};

export default VariantTemplatePreviewModalSettingsDetails;
